// SPDX-License-Identifier: AGPL-3.0-or-later

import {AGENT_THREAD_STATUSES} from '@fluxer/schema/src/domains/konvo/Agents';
import {DASHBOARD_ID_PATTERN} from '@fluxer/schema/src/domains/konvo/AssistantDashboard';
import {z} from 'zod';

export const ASSISTANT_RESPONSE_PROVIDER = 'Konvo:assistant_response';
export const ASSISTANT_RESPONSE_FIELD_PREFIX = 'response:';
export const ASSISTANT_RESPONSE_CHUNK_SIZE = 900;

const AssistantResponseActionSchema = z.object({
	label: z.string().trim().min(1).max(40),
	prompt: z.string().trim().min(1).max(1000),
	style: z.enum(['primary', 'secondary']).default('secondary'),
});

const TimelineBlockSchema = z.object({
	type: z.literal('timeline'),
	title: z.string().trim().min(1).max(120),
	subtitle: z.string().trim().max(160).optional(),
	items: z
		.array(
			z.object({
				kind: z.enum(['event', 'free']),
				start: z.string().trim().min(1).max(20),
				end: z.string().trim().min(1).max(20),
				title: z.string().trim().min(1).max(180),
				meta: z.string().trim().max(240).optional(),
				/** Known about but not on the calendar yet (confirmed by mail, say): hollow dot and a tag. */
				tentative: z.boolean().optional(),
				/** What needs the user about this item, in one line. Shown in the accent colour. */
				note: z.string().trim().max(200).optional(),
				/** One follow-up for this item, e.g. "Add to calendar". */
				action: AssistantResponseActionSchema.optional(),
			}),
		)
		.min(1)
		.max(20),
	actions: z.array(AssistantResponseActionSchema).max(3).optional(),
});

const CarouselBlockSchema = z.object({
	type: z.literal('carousel'),
	title: z.string().trim().min(1).max(120).optional(),
	items: z
		.array(
			z.object({
				id: z.string().trim().min(1).max(80),
				title: z.string().trim().min(1).max(160),
				subtitle: z.string().trim().max(200).optional(),
				body: z.string().trim().max(1200).optional(),
				badge: z.string().trim().max(40).optional(),
				actions: z.array(AssistantResponseActionSchema).max(2).optional(),
			}),
		)
		.min(2)
		.max(6),
});

const MeetingBlockSchema = z.object({
	type: z.literal('meeting'),
	phase: z.enum(['prepare', 'follow_up']),
	title: z.string().trim().min(1).max(180),
	when: z.string().trim().max(120).optional(),
	attendees: z.array(z.string().trim().min(1).max(100)).max(30).optional(),
	summary: z.string().trim().max(1200).optional(),
	sections: z
		.array(
			z.object({
				title: z.string().trim().min(1).max(80),
				items: z.array(z.string().trim().min(1).max(500)).min(1).max(10),
			}),
		)
		.max(6)
		.optional(),
	actions: z.array(AssistantResponseActionSchema).max(3).optional(),
});

/** One thing Konvo read: a mail message, a WhatsApp/Teams/Konvo message or a
 * ClickUp task. The header fields are a snapshot so the card renders without a
 * round trip; the ids let the preview window fetch the real thing. */
const SourceItemSchema = z.object({
	kind: z.enum(['mail', 'whatsapp', 'konvo', 'teams', 'clickup']),
	title: z.string().trim().min(1).max(200),
	subtitle: z.string().trim().max(200).optional(),
	meta: z.string().trim().max(120).optional(),
	snippet: z.string().trim().max(600).optional(),
	message_id: z.string().trim().max(400).optional(),
	chat_id: z.string().trim().max(200).optional(),
	channel_id: z.string().trim().max(40).optional(),
	task_id: z.string().trim().max(60).optional(),
	url: z.string().trim().max(2000).optional(),
});

const SourceBlockSchema = z.object({
	type: z.literal('source'),
	title: z.string().trim().max(120).optional(),
	items: z.array(SourceItemSchema).min(1).max(4),
});

/**
 * Something the assistant just made in the user's accounts: a calendar event or
 * a mail draft. The card's button opens it in Konvo (the event window, the
 * composer), so the reply never has to send the user to Outlook.
 */
export const ASSISTANT_ITEM_KINDS = ['event', 'mail_draft'] as const;

const ItemBlockSchema = z.object({
	type: z.literal('item'),
	kind: z.enum(ASSISTANT_ITEM_KINDS),
	/** The Graph id the app opens: an event id or a draft message id. */
	id: z.string().trim().min(1).max(400),
	/** Event subject or mail subject. */
	title: z.string().trim().min(1).max(200),
	/** ISO times; the app formats them in the reader's locale. */
	start: z.string().trim().max(40).optional(),
	end: z.string().trim().max(40).optional(),
	all_day: z.boolean().optional(),
	location: z.string().trim().max(200).optional(),
	/** Invitees for an event, To for a draft. */
	people: z.array(z.string().trim().min(1).max(120)).max(30).optional(),
	cc: z.array(z.string().trim().min(1).max(120)).max(30).optional(),
	/** The start of the description or of the draft's text. */
	snippet: z.string().trim().max(400).optional(),
	/** A draft that answers an existing mail, inside its thread. */
	in_thread: z.boolean().optional(),
	/** An event the assistant changed rather than created. */
	updated: z.boolean().optional(),
});

/** What woke the proactive engine up; the card shows it as a small label. */
export const ASSISTANT_BRIEF_KINDS = [
	'meeting_soon',
	'morning',
	'vip_mail',
	'task_overdue',
	'commitment_due',
	// KONVO: the recap after a Teams meeting Konvo followed (KonvoTeamsMeetingRecap).
	'meeting_recap',
] as const;

/**
 * Something the assistant surfaced on its own (a meeting brief, the morning
 * brief, a nudge). `item_id` ties the card to its engine record, so the app
 * can report when an action on it is used.
 */
export const ASSISTANT_BRIEF_SOURCES = [
	'mail',
	'whatsapp',
	'teams',
	'konvo',
	'clickup',
	'calendar',
	'github',
	'other',
] as const;
export const ASSISTANT_BRIEF_REF_KINDS = ['mail', 'chat', 'whatsapp', 'clickup'] as const;
export const ASSISTANT_BRIEF_ACTION_ICONS = ['draft', 'view', 'open', 'schedule', 'done'] as const;
export const ASSISTANT_BRIEF_FACT_ICONS = ['meetings', 'no_meetings', 'out', 'holiday', 'reminder'] as const;

/**
 * What a brief or to-do is about (a mail, a Konvo chat, a WhatsApp chat, a
 * ClickUp task). The app opens it in place; the server ticks to-dos off with it.
 * A brief's own mail ref is the message id; a to-do's is the conversation id.
 */
const BriefRefSchema = z.object({
	kind: z.enum(ASSISTANT_BRIEF_REF_KINDS),
	id: z.string().trim().min(1).max(300),
});

/**
 * One line of a checklist brief: what to do, why, where it came from, and at
 * most one action. The same shape serves the "good to know" lines, which have
 * no tick.
 */
const BriefItemSchema = z.object({
	title: z.string().trim().min(1).max(80),
	detail: z.string().trim().max(140).optional(),
	source: z.enum(ASSISTANT_BRIEF_SOURCES).optional(),
	/** Something is broken (a failing build, a system down): the one red dot. */
	alert: z.boolean().optional(),
	action: z
		.object({
			label: z.string().trim().min(1).max(40),
			prompt: z.string().trim().min(1).max(1000),
			icon: z.enum(ASSISTANT_BRIEF_ACTION_ICONS).default('open'),
		})
		.optional(),
	/**
	 * What the to-do is about (a mail conversation, a Konvo chat, a WhatsApp chat, a ClickUp task),
	 * so the server can tick it off once the user replied or closed the task.
	 */
	ref: BriefRefSchema.optional(),
});

/** A fact about the day built without the model: meetings, who is out. */
const BriefFactSchema = z.object({
	icon: z.enum(ASSISTANT_BRIEF_FACT_ICONS),
	text: z.string().trim().min(1).max(60),
	/** Names behind the fact (who is out), for initials and the tooltip. */
	people: z.array(z.string().trim().min(1).max(60)).max(8).optional(),
});

const BriefBlockSchema = z.object({
	type: z.literal('brief'),
	item_id: z.string().trim().min(1).max(40),
	kind: z.enum(ASSISTANT_BRIEF_KINDS),
	/** 'brief' is a context card, 'nudge' a one-liner with one action. */
	style: z.enum(['brief', 'nudge']).default('brief'),
	title: z.string().trim().min(1).max(160),
	when: z.string().trim().max(120).optional(),
	body: z.string().trim().max(1500),
	actions: z.array(AssistantResponseActionSchema).max(3).optional(),
	/** What the brief is about, so "Show me the mail" opens it right away instead of asking. */
	ref: BriefRefSchema.optional(),
	/** A checklist brief (the morning brief): `body` is then a one-line summary above the list. */
	todos: z.array(BriefItemSchema).max(4).optional(),
	notes: z.array(BriefItemSchema).max(3).optional(),
	facts: z.array(BriefFactSchema).max(4).optional(),
});

/** Icons for a findings item's actions; the label becomes the tooltip. */
export const ASSISTANT_FINDINGS_ACTION_ICONS = [
	'task',
	'reply',
	'assign',
	'done',
	'schedule',
	'log_time',
	'notes',
	'open',
] as const;

/**
 * One thing a starting point found ("Signed-off Pantone swatches"): why it matters,
 * how urgent it is, where it came from, and at most two follow-ups.
 */
const FindingsItemSchema = z.object({
	title: z.string().trim().min(1).max(120),
	detail: z.string().trim().max(240).optional(),
	/** Due date or lateness in words ("Due Fri 3 Oct", "2 days late"), shown in the urgency colour. */
	due: z.string().trim().max(40).optional(),
	/** high = due or blocking (red), medium = open (amber), low = can wait (grey). */
	urgency: z.enum(['high', 'medium', 'low']).default('medium'),
	sources: z.array(SourceItemSchema).max(3).optional(),
	actions: z
		.array(AssistantResponseActionSchema.extend({icon: z.enum(ASSISTANT_FINDINGS_ACTION_ICONS).default('open')}))
		.max(2)
		.optional(),
});

/**
 * The answer to a starting point (Konvo panel): a count first, what was checked, a
 * one-line summary, then the items in groups, most urgent first.
 */
const FindingsBlockSchema = z.object({
	type: z.literal('findings'),
	/** The answer in one line, e.g. "4 things Mountain Hops is waiting on". */
	title: z.string().trim().min(1).max(120),
	/** What was read, e.g. "Checked 23 emails, 6 tasks, 1 chat". */
	checked: z.string().trim().max(160).optional(),
	summary: z.string().trim().max(400).optional(),
	groups: z
		.array(
			z.object({
				title: z.string().trim().max(60).optional(),
				items: z.array(FindingsItemSchema).min(1).max(8),
			}),
		)
		.min(1)
		.max(4),
	/** One or two follow-ups for the whole answer ("Plan my week", "Log all 3"). */
	actions: z.array(AssistantResponseActionSchema).max(2).optional(),
});

/**
 * A chart or dashboard. The card carries only what a notification and a first paint need;
 * the spec and the computed numbers load from the dashboard endpoint, so an update to the
 * dashboard shows in every card that points at it. `share_id` is a frozen copy posted into a
 * chat, readable by that chat's members; `dashboard_id` is the owner's live dashboard.
 */
const DashboardBlockSchema = z.object({
	type: z.literal('dashboard'),
	dashboard_id: z.string().regex(DASHBOARD_ID_PATTERN).optional(),
	share_id: z.string().regex(DASHBOARD_ID_PATTERN).optional(),
	title: z.string().trim().min(1).max(80),
	subtitle: z.string().trim().max(160).optional(),
	/** Bumped when the dashboard changes, so the message update makes every card refetch. */
	version: z.number().int().min(1).max(10_000),
	panel_count: z.number().int().min(1).max(16),
});

/**
 * Something Konvo could not do, why, and the closest thing it can do instead: "I can't show
 * other people's logged time. Only owners and admins can. Show your own time instead?
 * [Yes, show my time] [No thanks]". The first action is the suggested one; declining only
 * answers the card on this device and sends nothing.
 */
const ChoiceBlockSchema = z.object({
	type: z.literal('choice'),
	/** What could not be done, in one sentence. */
	title: z.string().trim().min(1).max(160),
	/** Why, in one or two sentences. */
	reason: z.string().trim().max(320).optional(),
	/** The offer, e.g. "Show your own time instead?". */
	question: z.string().trim().max(160).optional(),
	/** The alternatives, best first; the first is the Yes. */
	actions: z.array(AssistantResponseActionSchema).min(1).max(3),
	/** A button that answers the card without sending anything, e.g. "No thanks". */
	decline_label: z.string().trim().min(1).max(40).optional(),
});

/**
 * KONVO: the person's coding agents (docs/konvo-agents/DESIGN.md). Konvo builds it from the live
 * thread lists (show_agents, agent notifications); the model never writes it. Only titles and
 * statuses, never thread content. Rows open /agents/:machineId/:threadId.
 */
const AgentsBlockSchema = z.object({
	type: z.literal('agents'),
	machines: z
		.array(
			z.object({
				id: z.string().min(1).max(100),
				name: z.string().trim().min(1).max(80),
				online: z.boolean(),
				lastSeenAt: z.string().max(40).nullable().optional(),
				/** The agent app on the machine, e.g. "T3 Code". */
				tool: z.string().max(40).optional(),
				/** Needs you first, then running (or the latest finished when nothing is). */
				threads: z
					.array(
						z.object({
							id: z.string().min(1).max(200),
							title: z.string().trim().min(1).max(120),
							project: z.string().trim().max(80).optional(),
							status: z.enum(AGENT_THREAD_STATUSES),
							needsYou: z.boolean(),
							/** The agent behind the thread (codex, claudeAgent, ...): picks the logo. */
							provider: z.string().max(40).optional(),
							updatedAt: z.string().max(40),
						}),
					)
					.max(12),
				/** Finished today and not listed: one "N finished today" row. */
				finishedToday: z.number().int().min(0).max(10_000).optional(),
				/** Rows left out to keep the card small. */
				more: z.number().int().min(0).max(10_000).optional(),
			}),
		)
		.min(1)
		.max(8),
});

/**
 * KONVO: plan usage of the person's coding agents (Claude, Codex, ...), as their machines last
 * reported it. Konvo builds it (show_agent_usage, usage warnings); the model never writes it.
 * Old apps that do not know it show the card's title and description.
 */
const AgentUsageWindowBlockSchema = z.object({
	label: z.string().trim().min(1).max(80),
	kind: z.string().max(40).optional(),
	used_percent: z.number().min(0).max(100),
	resets_at: z.string().max(40).optional(),
	/** When it runs out at the pace so far, if well before it resets. */
	runs_out_at: z.string().max(40).optional(),
});
const AgentUsageBlockSchema = z.object({
	type: z.literal('agent_usage'),
	/** A headline when Konvo sends it unasked, e.g. "Claude is at 90% of this session". */
	title: z.string().trim().min(1).max(120).optional(),
	plans: z
		.array(
			z.object({
				/** The agent tool's provider (claudeAgent, codex, ...): picks the logo. */
				provider: z.string().max(80),
				provider_name: z.string().trim().min(1).max(80),
				plan: z.string().trim().max(80).optional(),
				machines: z.array(z.string().trim().min(1).max(80)).max(8),
				checked_at: z.string().max(40),
				windows: z.array(AgentUsageWindowBlockSchema).min(1).max(10),
			}),
		)
		.min(1)
		.max(8),
	/** Threads the usage touches: still working (will pause) or paused on the limit. Rows open the thread. */
	threads: z
		.array(
			z.object({
				machine_id: z.string().min(1).max(100),
				id: z.string().min(1).max(200),
				title: z.string().trim().min(1).max(120),
				state: z.enum(['running', 'paused']),
			}),
		)
		.max(6)
		.optional(),
});

/**
 * KONVO: the agents digest (design/agents-side-bar r2-d1): what the person's coding agents did since
 * the previous digest, sent on weekday mornings and when they ask "what did my agents do?". Konvo
 * builds it from the machines' thread lists and GitHub (konvo/agents/AgentsDigestCard.ts); the model
 * never writes it. Rows come most important first: needs you, pull requests ready, failed. Old apps
 * that do not know it show the card's title and description, which say the same in words.
 */
export const AGENTS_DIGEST_ROW_KINDS = ['approval', 'question', 'needs_you', 'pr', 'failed'] as const;
const AgentsDigestThreadSchema = z.object({
	machine_id: z.string().min(1).max(100),
	thread_id: z.string().min(1).max(200),
	title: z.string().trim().min(1).max(120),
});
const AgentsDigestRowSchema = AgentsDigestThreadSchema.extend({
	kind: z.enum(AGENTS_DIGEST_ROW_KINDS),
	project: z.string().trim().max(80).optional(),
	/** Waiting since, failed at or finished at. */
	at: z.string().max(40).optional(),
	/** An approval's command or change, cut to fit; `command_cut` says it was. */
	command: z.string().max(240).optional(),
	command_cut: z.boolean().optional(),
	/** Set when the machine lets Konvo answer approvals: Allow answers this request. */
	request_id: z.string().max(200).optional(),
	/** The machine lets Konvo send prompts (Try again). */
	can_send: z.boolean().optional(),
	/** Why a failed thread stopped, as the agent tool said it. */
	error: z.string().max(200).optional(),
	limit: z.enum(['usage', 'credits']).optional(),
	pr: z
		.object({
			repository: z.string().max(200),
			number: z.number().int().positive(),
			url: z.string().max(500),
			files: z.number().int().nonnegative().optional(),
			/** passed: every check passed; none: the pull request has no checks. */
			checks: z.enum(['passed', 'none']),
			/** Konvo's review found this many things to look at (or blocking). */
			review_notes: z.number().int().nonnegative().optional(),
			/** The person may merge it from Konvo (the repository's merge rights). */
			can_merge: z.boolean().optional(),
		})
		.optional(),
});
const AgentsDigestBlockSchema = z.object({
	type: z.literal('agents_digest'),
	/** The start of what it covers (the previous digest, or 18:00 the day before). */
	since: z.string().max(40),
	/** When Konvo put it together. */
	at: z.string().max(40),
	counts: z.object({
		finished: z.number().int().nonnegative(),
		needs_you: z.number().int().nonnegative(),
		failed: z.number().int().nonnegative(),
		prs_ready: z.number().int().nonnegative(),
	}),
	rows: z.array(AgentsDigestRowSchema).max(8),
	/** Finished threads not on a row, newest first: the folded "N more finished". */
	finished: z.array(AgentsDigestThreadSchema).max(8),
	/** Finished threads beyond the ones listed. */
	finished_more: z.number().int().nonnegative().optional(),
	/** Rows left out to keep the card small. */
	rows_more: z.number().int().nonnegative().optional(),
	offline: z
		.array(
			z.object({
				id: z.string().min(1).max(100),
				name: z.string().trim().min(1).max(80),
				last_seen_at: z.string().max(40).nullable(),
			}),
		)
		.max(8),
	/**
	 * The footer: agent time in the window per client (Time and client), from the chats whose time
	 * Konvo has read; most first, a null name is "No client". Left out when there is none.
	 */
	time: z
		.object({
			/** The window crosses one night: "Agent time overnight". */
			overnight: z.boolean(),
			total_ms: z.number().int().nonnegative(),
			clients: z
				.array(z.object({name: z.string().trim().min(1).max(80).nullable(), ms: z.number().int().nonnegative()}))
				.max(6),
			/**
			 * The person's own minutes in the window per client, the same way (estimated from their
			 * prompts, answers and the code they reviewed, unless they set a chat's minutes). Left out
			 * under a minute.
			 */
			you: z
				.object({
					total_ms: z.number().int().nonnegative(),
					clients: z
						.array(z.object({name: z.string().trim().min(1).max(80).nullable(), ms: z.number().int().nonnegative()}))
						.max(6),
				})
				.optional(),
		})
		.optional(),
});

/**
 * The end-of-day wrap-up plan (WrapUp.ts). The card reads the live plan by `plan_id`, so ticking a
 * row in the corner or changing it in words updates this message too. The counts and titles here
 * are what old apps and push notifications show.
 */
const WrapUpBlockSchema = z.object({
	type: z.literal('wrap_up'),
	plan_id: z.string().max(40),
	day: z.string().max(10),
	/** E.g. "Friday". */
	next_day_label: z.string().max(40),
	agents: z.array(z.string().max(160)).max(8),
	tasks: z.array(z.string().max(255)).max(10),
});

export const AssistantResponseBlockSchema = z.discriminatedUnion('type', [
	TimelineBlockSchema,
	CarouselBlockSchema,
	MeetingBlockSchema,
	SourceBlockSchema,
	ItemBlockSchema,
	BriefBlockSchema,
	FindingsBlockSchema,
	DashboardBlockSchema,
	ChoiceBlockSchema,
	AgentsBlockSchema,
	AgentUsageBlockSchema,
	AgentsDigestBlockSchema,
	WrapUpBlockSchema,
]);

export const AssistantResponseSchema = z.object({
	version: z.literal(1),
	blocks: z.array(AssistantResponseBlockSchema).min(1).max(6),
});

export type AssistantResponseAction = z.infer<typeof AssistantResponseActionSchema>;
export type AssistantResponseBlock = z.infer<typeof AssistantResponseBlockSchema>;
export type AssistantResponse = z.infer<typeof AssistantResponseSchema>;
export type AssistantTimelineBlock = z.infer<typeof TimelineBlockSchema>;
export type AssistantCarouselBlock = z.infer<typeof CarouselBlockSchema>;
export type AssistantMeetingBlock = z.infer<typeof MeetingBlockSchema>;
export type AssistantSourceBlock = z.infer<typeof SourceBlockSchema>;
export type AssistantItemBlock = z.infer<typeof ItemBlockSchema>;
export type AssistantBriefBlock = z.infer<typeof BriefBlockSchema>;
export type AssistantDashboardBlock = z.infer<typeof DashboardBlockSchema>;
export type AssistantChoiceBlock = z.infer<typeof ChoiceBlockSchema>;
export type AssistantAgentsBlock = z.infer<typeof AgentsBlockSchema>;
export type AssistantAgentUsageBlock = z.infer<typeof AgentUsageBlockSchema>;
export type AssistantAgentsDigestBlock = z.infer<typeof AgentsDigestBlockSchema>;
export type AssistantWrapUpBlock = z.infer<typeof WrapUpBlockSchema>;
export type AssistantAgentsDigestRow = z.infer<typeof AgentsDigestRowSchema>;
export type AssistantBriefKind = (typeof ASSISTANT_BRIEF_KINDS)[number];
export type AssistantBriefItem = z.infer<typeof BriefItemSchema>;
export type AssistantBriefFact = z.infer<typeof BriefFactSchema>;
export type AssistantBriefSource = (typeof ASSISTANT_BRIEF_SOURCES)[number];
export type AssistantBriefRef = NonNullable<AssistantBriefItem['ref']>;
export type AssistantSourceItem = z.infer<typeof SourceItemSchema>;
export type AssistantFindingsBlock = z.infer<typeof FindingsBlockSchema>;
export type AssistantFindingsItem = z.infer<typeof FindingsItemSchema>;
export const AssistantSourceItemSchema = SourceItemSchema;

export function encodeAssistantResponse(response: AssistantResponse): Array<string> {
	const json = JSON.stringify(AssistantResponseSchema.parse(response));
	const chunks: Array<string> = [];
	for (let offset = 0; offset < json.length; offset += ASSISTANT_RESPONSE_CHUNK_SIZE) {
		chunks.push(json.slice(offset, offset + ASSISTANT_RESPONSE_CHUNK_SIZE));
	}
	return chunks;
}

export function decodeAssistantResponseFields(
	fields: ReadonlyArray<{name?: string | null; value?: string | null}> | null | undefined,
): AssistantResponse | null {
	const chunks = (fields ?? [])
		.flatMap((field) => {
			if (!field.name?.startsWith(ASSISTANT_RESPONSE_FIELD_PREFIX) || typeof field.value !== 'string') return [];
			const index = Number(field.name.slice(ASSISTANT_RESPONSE_FIELD_PREFIX.length));
			return Number.isInteger(index) && index >= 0 ? [{index, value: field.value}] : [];
		})
		.sort((left, right) => left.index - right.index);
	if (chunks.length === 0 || chunks.some((chunk, index) => chunk.index !== index)) return null;
	try {
		return AssistantResponseSchema.parse(JSON.parse(chunks.map((chunk) => chunk.value).join('')));
	} catch {
		return null;
	}
}
